import type { DashboardLayout } from '@tempo-io/roadmap-dto';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useExportMode } from '@/common/components/ui/swimlane/context';
import { DashboardGridResizeHandles } from './DashboardGridResizeHandles';
import { type GridSizingPanel, useDashboardGridSizing } from './useDashboardGridSizing';

export const DashboardGrid = ({
  dashboardId,
  layout,
  panels,
  scrollContainer,
  renderCell,
  targetHeightPx,
}: {
  dashboardId: number;
  layout: DashboardLayout;
  panels: GridSizingPanel[];
  scrollContainer: HTMLElement | null;
  renderCell: (position: number) => ReactNode;
  targetHeightPx?: number | null;
}) => {
  const sizingApi = useDashboardGridSizing(
    dashboardId,
    layout,
    panels,
    scrollContainer,
    targetHeightPx,
  );
  const { descriptor, sizing } = sizingApi;
  const [gridEl, setGridEl] = useState<HTMLDivElement | null>(null);
  const { isExporting } = useExportMode();

  const isRow = descriptor.orientation === 'row';

  return (
    <div>
      <div className="relative w-full">
        <div
          ref={setGridEl}
          className={isRow ? 'flex flex-col gap-4' : 'flex flex-row gap-4'}
          style={isRow ? undefined : { width: '100%' }}
        >
          {descriptor.stripes.map((stripe, stripeIdx) => {
            const stripeSize = sizing.stripes[stripeIdx];
            if (!stripeSize) return null;
            // Flex weights distribute post-gap track space by fraction; `width: %` would include the gap and drift handle math.
            return (
              <div
                key={`stripe-${stripe.cells[0]?.position ?? stripeIdx}`}
                className={isRow ? 'flex flex-row gap-4' : 'flex flex-col gap-4'}
                style={
                  isRow
                    ? { height: `${stripeSize.primarySize}px`, width: '100%' }
                    : { flex: `${stripeSize.primarySize} 1 0`, minWidth: 0 }
                }
              >
                {stripe.cells.map((cell, cellIdx) => {
                  const crossSize = stripeSize.cellCrossSizes[cellIdx] ?? 0;
                  return (
                    <div
                      key={`cell-${cell.position}`}
                      className="min-h-0 min-w-0"
                      style={
                        isRow
                          ? { flex: `${crossSize} 1 0`, minWidth: 0 }
                          : { height: `${crossSize}px` }
                      }
                    >
                      {renderCell(cell.position)}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        {/* The last handle's hitbox hangs past the grid, which would grow a pinned canvas. */}
        {!isExporting && (
          <DashboardGridResizeHandles
            gridEl={gridEl}
            sizingApi={sizingApi}
            scrollContainer={scrollContainer}
          />
        )}
      </div>
    </div>
  );
};
